import React from "react";
import { Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../components/ui/detailView";

export type CatalogItem = {
  title?: string;
  code?: string | null;
  color?: string | null;
  description?: string;
  created_at?: string;
  updated_at?: string;
};

/** The chip an issue / service is shown as on jobs, in its configured colour. */
export const CatalogItemChip = ({ item }: { item: CatalogItem }) => (
  <Chip
    label={item.title}
    size="small"
    sx={
      item.color
        ? { bgcolor: item.color, color: "white", "& .MuiChip-label": { color: "white" } }
        : { bgcolor: "rgba(128, 128, 128, 0.25)", color: "text.secondary" }
    }
  />
);

/** Detail view shared by the issues and services catalogs. `extraRows` holds
 * the rows only one of them has (e.g. a service's price), shown above the
 * description. */
export const CatalogItemDetail = ({
  item,
  icon,
  extraRows,
}: {
  item: CatalogItem;
  icon: React.ReactNode;
  extraRows?: React.ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <DetailPage>
      <DetailHeader
        icon={icon}
        title={item.title}
        subtitle={item.code && `${t("Code")}: ${item.code}`}
      />

      <DetailSection title={t("Basic Information")}>
        <DetailRow label={t("Title")} value={item.title && <CatalogItemChip item={item} />} />
        {item.code && (
          <DetailRow label={t("Code")}>
            <Typography variant="subtitle1" sx={{ fontFamily: "monospace" }}>
              {item.code}
            </Typography>
          </DetailRow>
        )}
        {extraRows}
        {item.description && <DetailRow label={t("Description")} value={item.description} multiline />}
      </DetailSection>

      <DetailMetadata createdAt={item.created_at} updatedAt={item.updated_at} />
    </DetailPage>
  );
};
